<template>
  <div class="flight-progress">
    <!-- 左侧控制区域 -->
    <div class="p-left">
      <i class="iconfont icon-zhongzhi" @click="resetProgress"></i>
      <div class="pause-resume-btn">
        <i
          v-if="!isPlaying"
          class="iconfont icon-play icon-btn resume"
          @click="togglePlay('resume')"
        ></i>
        <i
          v-if="isPlaying"
          class="iconfont icon-icon-pause icon-btn pause"
          @click="togglePlay('pause')"
        ></i>
      </div>
      <div class="play-time ellipsis">
        <span>{{ formatTime(currentTimeReset) }}</span>
        <span> / </span>
        <span>{{ formatTime(totalTimeReset) }}</span>
      </div>
    </div>

    <!-- 进度条区域 -->
    <div
      ref="barRef"
      class="bar"
      @click="handleBarClick"
      @mousemove="handleMouseMove"
    >
      <!-- 已播放进度 -->
      <div ref="lightBarRef" class="light-bar"></div>

      <!-- 进度点（可拖拽） -->
      <div class="light-point" @mousedown="startDrag"></div>

      <!-- 航点标记 -->
      <div
        v-for="(waypoint, index) in timeInfo.waypointInfo"
        :key="`_${waypoint.startTime}_${index}`"
        :id="`waypoint_${waypoint.startTime}_${index}`"
        class="waypoint"
        @mouseover="showWaypointActions(index, $event)"
        @mouseout="hideWaypointActions"
      ></div>

      <!-- 航点动作弹窗 -->
      <div
        v-if="waypointActions.actions.length > 0 && showActionPop"
        id="$_action-pop"
        class="action-pop"
        :style="{ left: actionPopLeft }"
      >
        <i
          v-for="(action, idx) in waypointActions.actions"
          :key="action.endTime"
          class="iconfont"
          :class="`${WaylineActionTypeIcon[action.type]} ${idx}`"
        ></i>
      </div>
    </div>

    <!-- 右侧控制区域 -->
    <div class="p-right">
      <!-- 倍速选择 -->
      <div class="multiple-btn-options">
        <div class="margin16 mulipute btn" @click="toggleSpeedOptions">
          {{ currentMultiple }}x
        </div>
        <div v-if="showSpeedOptions" class="speed-options">
          <div
            v-for="option in speedOptions"
            :key="option.value"
            :class="['multiple-option', { 'multiple-option-active': currentMultiple === option.value }]"
            @click="selectSpeed(option.value)"
          >
            {{ option.label }}
          </div>
        </div>
      </div>

      <!-- 结束飞行按钮 -->
      <div class="end-flight" @click="endFlight">
        <div class="icon"></div>
        <div class="text">结束</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, watch, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue'
import { formatTime } from '/@/utils/time-formatter' // 假设有这个工具函数
import dayjs from 'dayjs'
import { WaylineActionTypeIcon } from '/@/types/wayline'

// Props
const props = defineProps({
  isShowRSF: {
    type: Boolean,
    default: false
  },
  currentTimeReset: {
    type: Number,
    default: 0
  },
  totalTimeReset: {
    type: Number,
    default: 0
  },
  progressRate: {
    type: Number,
    default: 0
  },
  timeInfo: {
    type: Object,
    default: () => ({})
  }
})

// Emits
const emit = defineEmits([
  'progressChange',
  'updateTimeMultiple',
  'endFlight',
  'videoPlayStatus'
])

// Refs
const barRef = ref(null)
const lightBarRef = ref(null)
const isPlaying = ref(false)
const isDragging = ref(false)
const currentMultiple = ref(1)
const showSpeedOptions = ref(false)
const waypointActions = ref({ actions: [], startTime: 0, waypointIndex: 0 })
const showActionPop = ref(false)
const actionPopLeft = ref('0px')

// 倍速选项
const speedOptions = [
  { value: 0.5, label: '0.5x' },
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 4, label: '4x' }
]

// 监听进度变化
watch(() => props.progressRate, (newVal) => {
  if (newVal === 1) {
    togglePlay('pause', 'auto')
  }

  if (lightBarRef.value) {
    const barWidth = barRef.value?.clientWidth || 0
    const progressWidth = newVal * barWidth
    lightBarRef.value.style.width = `${progressWidth}px`

    // 更新进度点位置
    const lightPoint = barRef.value?.querySelector('.light-point')
    if (lightPoint) {
      lightPoint.style.left = `${progressWidth}px`
    }
  }
})

// 监听RSF显示状态
watch(() => props.isShowRSF, (isShow) => {
  if (isShow) {
    updateWaypointMarkers()
  } else {
    togglePlay('pause')
    resetProgress()
  }
})

// 方法
const togglePlay = (status, auto = false) => {
  emit('videoPlayStatus', status)

  if (auto && status === 'pause') {
    isPlaying.value = false
    return
  }

  if (props.progressRate === 1) {
    resetProgress()
  }

  isPlaying.value = status === 'resume'
}

const resetProgress = () => {
  if (lightBarRef.value) {
    lightBarRef.value.style.width = '0px'
    const lightPoint = barRef.value?.querySelector('.light-point')
    if (lightPoint) {
      lightPoint.style.left = '0px'
    }
  }
  emit('progressChange', 0)
}

const handleBarClick = (event) => {
  if (isDragging.value) return

  let clickX = 0
  const targetId = event.target?.id

  if (targetId && targetId.includes('waypoint_')) {
    clickX = event.target.style.left.split('px')[0] || event.target.style.left
  } else {
    clickX = event.offsetX
  }

  updateProgress(clickX)
}

const startDrag = (event) => {
  isDragging.value = true
  const startX = event.clientX
  const initialWidth = lightBarRef.value?.offsetWidth || 0

  const handleMouseMove = (moveEvent) => {
    const deltaX = moveEvent.clientX - startX
    const newWidth = initialWidth + deltaX
    updateProgress(newWidth)
  }

  const handleMouseUp = () => {
    isDragging.value = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    barRef.value?.removeEventListener('mouseleave', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  barRef.value?.addEventListener('mouseleave', handleMouseUp)
}

const updateProgress = (width) => {
  const barWidth = barRef.value?.clientWidth || 0
  let progressWidth = width

  // 边界检查
  if (progressWidth > barWidth) progressWidth = barWidth
  if (progressWidth < 0) progressWidth = 0

  // 更新UI
  if (lightBarRef.value) {
    lightBarRef.value.style.width = `${progressWidth}px`
    const lightPoint = barRef.value?.querySelector('.light-point')
    if (lightPoint) {
      lightPoint.style.left = `${progressWidth}px`
    }
  }

  // 计算进度百分比
  const progressRate = progressWidth / barWidth
  emit('progressChange', progressRate)
}

const updateWaypointMarkers = () => {
  if (!props.timeInfo) return

  const barWidth = barRef.value?.clientWidth || 0
  const totalTime = props.timeInfo.totalTime
  const startTime = dayjs(props.timeInfo.startTime)

  props.timeInfo.waypointInfo?.forEach((waypoint, index) => {
    let waypointTime = dayjs(waypoint.startTime).diff(startTime, 'second') / totalTime * barWidth
    if (waypointTime > 0 && waypointTime > barWidth) {
      waypointTime = barWidth
    }
    const marker = document.getElementById(`waypoint_${waypoint.startTime}_${index}`)
    if (marker) {
      marker.style.left = `${waypointTime >= 0 ? waypointTime : 0}px`
    }
  })
}

const showWaypointActions = (waypointIndex, event) => {
  const waypoint = props.timeInfo.waypointInfo?.find(w => w.waypointIndex === waypointIndex)

  if (waypoint) {
    waypointActions.value.actions = waypoint.actionTimes?.map(time => ({
      type: time.type,
      endTime: time.endTime
    })) || []
  }

  actionPopLeft.value = event.target.style.left
  showActionPop.value = true
}

const hideWaypointActions = () => {
  showActionPop.value = false
}

const toggleSpeedOptions = () => {
  showSpeedOptions.value = !showSpeedOptions.value
}

const selectSpeed = (speed) => {
  currentMultiple.value = speed
  emit('updateTimeMultiple', speed)
  showSpeedOptions.value = false
}

const endFlight = () => {
  emit('endFlight')
  currentMultiple.value = 1
}

// 生命周期
onMounted(() => {
  window.addEventListener('resize', updateWaypointMarkers)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWaypointMarkers)
})

defineExpose({
  currentMultiple,
  updateWaypointMarkers
})
</script>

<style scoped>
.flight-progress {
        position: absolute;
    left: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    padding: 0 40px;
    height: 70px;
    bottom:32px;
    width: 100%;
    background: #0009;
    pointer-events: auto;
    color: #fff;
}

.p-left {
      display: flex;
    align-items: center;
    height: 100%;
    width: 176px;
  gap: 10px;

}

.icon-zhongzhi {
  cursor: pointer;
  font-size: 16px;
  color: #fff;
}

.pause-resume-btn {
  display: flex;
  gap: 5px;
}

.icon-btn {
  cursor: pointer;
  font-size: 16px;
  color: #fff;
  transition: color 0.3s;
}

.icon-btn:hover {
  color: #1890ff;
}

.play-time {
  font-family: monospace;
  color: #ffffffd9;
}

.bar {
        flex: 1;
    position: relative;
    margin-top: 2px;
    height: 8px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 24px;
    box-sizing: border-box;
    cursor: pointer;
}

.light-bar {
  position: absolute;
  height: 100%;
  background-color: #1890ff;
  border-radius: 4px;
  transition: width 0.1s linear;
}

.light-point {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background-color: #1890ff;
  border-radius: 50%;
  cursor: grab;
  z-index: 2;
}

.light-point:active {
  cursor: grabbing;
}

.waypoint {
  position: absolute;
    top: 0;
    z-index: 10;
    transform: translate(-50%);
    width: 8px;
    height: 8px;
    border: 1px solid var(--colorTextBase);
    box-sizing: border-box;
    border-radius: 5px;
    background: var(--colorPrimary);
}

.action-pop {
  position: absolute;
    top: -28px;
    transform: translate(-50%);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 8px;
    min-width: 30px;
    height: 24px;
    box-sizing: border-box;
    background: var(--colorFill);
    border-radius: 20px;
}
.action-pop .iconfont{
  margin-right: 8px;
    color: #fff;
    font-size: 13px;
}
.p-right {
  display: flex;
    align-items: center;
    padding-left: 24px;
    height: 100%;
    width: 155px;
    gap: 10px;
}

.multiple-btn-options {
  position: relative;
}

.btn {
  padding: 4px 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.speed-options {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-top: 5px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  overflow: hidden;
  z-index: 1000;
}

.multiple-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.multiple-option:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.multiple-option-active {
  background-color: #1890ff;
  color: white;
}

.end-flight {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background-color: #ff4d4f;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.end-flight:hover {
  background-color: #ff7875;
}

.end-flight .icon {
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 2px;
}

.end-flight .text {
  color: white;
  font-weight: 500;
}
</style>
